import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    inject,
    effect,
} from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import {
    ILeadSourceData,
    INewLeadsCreatedApiResponse,
    INewLeadsCreatedApiData,
} from '@app/core/models/chart.model'
import { AppConstants } from '@app/core/constants/app.constants'
import { dashboardEndpoints } from '@app/core/constants/valueConstants'
import { EChartType, ELeadSourceType } from '@app/core/enum/chart.enum'
import { map } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import { UserStore } from '@core/services/user/user.store'
import { getUserId } from '@app/core/helpers/user.helper'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import { EiTooltipDirective } from '../../../../shared/directives'
import {
    ApexNonAxisChartSeries,
    ApexResponsive,
    ApexChart,
    ApexTheme,
    ApexLegend,
    ApexDataLabels,
    ApexTooltip,
    ApexPlotOptions,
    ChartComponent,
} from 'ng-apexcharts'
import { ICON_PATHS } from '@app/core/constants/assets.constants'

export interface ChartOptions {
    series: ApexNonAxisChartSeries
    chart: ApexChart
    responsive: ApexResponsive[]
    labels: string[]
    theme: ApexTheme
    legend: ApexLegend
    dataLabels: ApexDataLabels
    tooltip: ApexTooltip
    colors: string[]
    plotOptions: ApexPlotOptions
}

@Component({
    selector: 'app-lead-sources',
    templateUrl: './lead-sources.component.html',
    styleUrls: ['./lead-sources.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EiTooltipDirective, ChartComponent],
})
export class LeadSourcesComponent implements OnInit {
    private readonly http = inject(HttpClient)
    private readonly cdr = inject(ChangeDetectorRef)
    private readonly userStore = inject(UserStore)
    private readonly dashboardService = inject(IDashboardService)
    private lastAcademicYearStart: number | null = null

    readonly AppConstants = AppConstants
    readonly ELeadSourceType = ELeadSourceType
    readonly infoIcon = ICON_PATHS.INFO

    readonly CHART_COLORS = {
        [ELeadSourceType.NEW_CLIENTS]: '#E77258',
        [ELeadSourceType.EXISTING_CLIENTS]: '#5DC688',
        [ELeadSourceType.PAST_CLIENTS]: '#FFEA62',
    } as const

    chartOptions: ChartOptions | null = null
    isLoading = true
    error: string | null = null

    constructor() {
        effect(() => {
            const academicYearStart = this.dashboardService.selectedAcademicYearStart()
            if (this.lastAcademicYearStart === null) {
                this.lastAcademicYearStart = academicYearStart
                return
            }
            if (academicYearStart === this.lastAcademicYearStart) {
                return
            }
            this.lastAcademicYearStart = academicYearStart
            this.loadChartData()
        })
    }

    ngOnInit(): void {
        this.loadChartData()
    }

    getTooltipText(): string {
        return `Distribution of leads by client type:\n• Past Clients – ${AppConstants.PAST_CLIENTS_DESCRIPTION}\n• Existing Clients – ${AppConstants.EXISTING_CLIENTS_DESCRIPTION}\n• New Clients – ${AppConstants.NEW_CLIENTS_DESCRIPTION}`
    }

    private loadChartData(): void {
        const userId = getUserId(this.userStore)
        if (!userId) {
            this.error = 'User ID not available'
            this.isLoading = false
            this.cdr.markForCheck()
            return
        }

        const currentYear = this.dashboardService.selectedAcademicYearStart()
        const apiUrl = `${environment.apiBaseUrl}${dashboardEndpoints.LEADS_CREATED_PATH}`

        // Build query parameters - only user_id and current_year as requested
        let params = new HttpParams()
        params = params.set('user_id', userId)
        params = params.set('current_year', currentYear.toString())

        this.http
            .get<INewLeadsCreatedApiResponse>(apiUrl, { params })
            .pipe(
                map((response) => {
                    // Transform API response to ILeadSourceData[] format
                    const leadSourceData = this.transformApiResponseToLeadSourceData(response.data)
                    return this.transformDataToChartOptions(leadSourceData)
                }),
            )
            .subscribe({
                next: (options) => {
                    this.chartOptions = options
                    this.isLoading = false
                    this.cdr.markForCheck()
                },
                error: (error) => {
                    this.error = 'Failed to load lead sources data'
                    this.isLoading = false
                    this.cdr.markForCheck()
                },
            })
    }

    /**
     * Transform API response data to ILeadSourceData[] format
     * Calculates percentages based on totalClients
     */
    private transformApiResponseToLeadSourceData(
        apiData: INewLeadsCreatedApiData,
    ): ILeadSourceData[] {
        const { newClients, existingClients, pastClients, totalClients } = apiData

        // Calculate percentages (handle division by zero)
        const calculatePercentage = (count: number, total: number): number => {
            if (!total) return 0
            return Math.round((count / total) * 100 * 100) / 100
        }

        const newClientsPercentage = calculatePercentage(newClients, totalClients)
        const existingClientsPercentage = calculatePercentage(existingClients, totalClients)
        const pastClientsPercentage = calculatePercentage(pastClients, totalClients)

        return [
            {
                name: ELeadSourceType.NEW_CLIENTS,
                value: newClientsPercentage,
                count: newClients,
                color: this.CHART_COLORS[ELeadSourceType.NEW_CLIENTS],
            },
            {
                name: ELeadSourceType.EXISTING_CLIENTS,
                value: existingClientsPercentage,
                count: existingClients,
                color: this.CHART_COLORS[ELeadSourceType.EXISTING_CLIENTS],
            },
            {
                name: ELeadSourceType.PAST_CLIENTS,
                value: pastClientsPercentage,
                count: pastClients,
                color: this.CHART_COLORS[ELeadSourceType.PAST_CLIENTS],
            },
        ]
    }

    private transformDataToChartOptions(data: ILeadSourceData[]): ChartOptions {
        const series = data.map((item) => item.value)
        const labels = data.map((item) => item.name)
        const colors = data.map((item) => item.color)

        return {
            series,
            chart: {
                type: EChartType.PIE,
                height: 350,
                animations: {
                    enabled: true,
                    speed: 800,
                },
            },
            labels,
            colors,
            theme: {
                mode: 'light',
            },
            legend: {
                position: 'bottom',
                horizontalAlign: 'center',
                fontSize: '14px',
                fontFamily: 'Nunito, sans-serif',
                fontWeight: 500,
                markers: {
                    strokeWidth: 0,
                    shape: 'circle',
                    offsetX: 0,
                    offsetY: 0,
                },
                itemMargin: {
                    horizontal: 15,
                    vertical: 5,
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) => {
                    return `${val.toFixed(2)}%`
                },
                style: {
                    fontSize: '1rem',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: 600,
                },
                dropShadow: {
                    enabled: false,
                },
            },
            plotOptions: {
                pie: {
                    dataLabels: {
                        offset: -30,
                    },
                    expandOnClick: false,
                },
            },
            tooltip: {
                enabled: true,
                shared: false,
                followCursor: true,
                intersect: false,
                fillSeriesColor: false,
                theme: 'light',
                style: {
                    fontSize: '14px',
                    fontFamily: 'Nunito, sans-serif',
                },
                y: {
                    formatter: (
                        val: number,
                        opts: { seriesIndex: number; w: { globals: { series: number[][] } } },
                    ) => {
                        const dataPoint = data[opts.seriesIndex]
                        const count = dataPoint.count
                        const percentage = val.toFixed(2)
                        return `${count} leads (${percentage}%)`
                    },
                    title: {
                        formatter: (seriesName: string) => seriesName,
                    },
                },
                marker: {
                    show: true,
                },
            },
            responsive: [
                {
                    breakpoint: 768,
                    options: {
                        chart: {
                            height: 300,
                        },
                        legend: {
                            fontSize: '12px',
                        },
                    },
                },
            ],
        }
    }
}
